/*
=========================================================
* RingFoods AI Voice Homepage
=========================================================

* Product: RingFoods AI Voice Agent for Restaurants
* Copyright 2024 RingFoods

=========================================================
*/

import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";

import Card from "@mui/material/Card";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Fab from "@mui/material/Fab";
import Fade from "@mui/material/Fade";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import YouTubeIcon from "@mui/icons-material/YouTube";
import InstagramIcon from "@mui/icons-material/Instagram";

// Material Kit 2 PRO React components
import MKBox from "components/MKBox";
import MKTypography from "components/MKTypography";
import MKButton from "components/MKButton";

// Material Kit 2 PRO React examples
import DefaultNavbar from "examples/Navbars/DefaultNavbar";
import PromoBanner from "components/PromoBanner";
import PromoPopup from "components/PromoPopup";
import { APP_BRAND, APP_VERSION } from "version";

// RingFoods Homepage sections
import Hero from "pages/RingFoodsHomepage/sections/Hero";
import PainPoints from "pages/RingFoodsHomepage/sections/PainPoints";
import StressShield from "pages/RingFoodsHomepage/sections/StressShield";
import Features from "pages/RingFoodsHomepage/sections/Features";
import FreeBusinessPage from "pages/RingFoodsHomepage/sections/FreeBusinessPage";
import FAQ, { faqs } from "pages/RingFoodsHomepage/sections/FAQ";
import HowItWorks from "pages/RingFoodsHomepage/sections/HowItWorks";
import Pricing from "pages/RingFoodsHomepage/sections/Pricing";
import Integrations from "pages/RingFoodsHomepage/sections/Integrations";
import ROICalculator from "pages/RingFoodsHomepage/sections/ROICalculator";
import CustomFeatures from "pages/RingFoodsHomepage/sections/CustomFeatures";
import Personalization from "pages/RingFoodsHomepage/sections/Personalization";
import Visibility from "pages/RingFoodsHomepage/sections/Visibility";
import FinalCTA from "pages/RingFoodsHomepage/sections/FinalCTA";

import { brandPrimary, grey100 } from "pages/RingFoodsHomepage/theme";
import useSignupUrl from "hooks/useSignupUrl";
import { getDisplayTrialDays } from "constants/promo";

// Shared brand-aware footer (single source of truth across all public pages).
import SiteFooter from "components/SiteFooter";

// Logo
import ringfoodsLogo from "assets/images/logos/ringfoods_logo.svg";

// Unsplash URL helper — WebP format, compressed, responsive
function responsiveImg(url, quality, width) {
  var q = quality || 60;
  var w = width || 1200;
  return url
    .replace(/w=\d+/, "w=" + w)
    .replace(/q=\d+/, "q=" + q)
    + (url.includes("fm=") ? "" : "&fm=webp");
}

// Image Banner Component
function ImageBanner({ imageUrl, title, subtitle, height = "300px", overlay = true }) {
  var mobileUrl = responsiveImg(imageUrl, 50, 600);
  var desktopUrl = responsiveImg(imageUrl, 60, 1200);
  var overlayGrad = "linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5))";
  return (
    <MKBox
      sx={{
        backgroundImage: overlay
          ? { xs: `${overlayGrad}, url(${mobileUrl})`, md: `${overlayGrad}, url(${desktopUrl})` }
          : { xs: `url(${mobileUrl})`, md: `url(${desktopUrl})` },
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundAttachment: { md: "fixed", xs: "scroll" },
        height: height,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {(title || subtitle) && (
        <Container>
          <Grid container justifyContent="center">
            <Grid item xs={12} md={8} textAlign="center">
              {title && (
                <MKTypography variant="h3" color="white" mb={1} sx={{ fontSize: { xs: "1.5rem", md: "2.5rem" } }}>
                  {title}
                </MKTypography>
              )}
              {subtitle && (
                <MKTypography variant="body1" color="white" opacity={0.9}>
                  {subtitle}
                </MKTypography>
              )}
            </Grid>
          </Grid>
        </Container>
      )}
    </MKBox>
  );
}

// Sticky CTA Component
function StickyCTA({ visible }) {
  const signupUrl = useSignupUrl();
  return (
    <Fade in={visible}>
      <MKBox
        sx={{
          position: "fixed",
          bottom: { xs: 16, md: 24 },
          right: { xs: 16, md: 24 },
          zIndex: 1000,
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
      >
        <MKButton
          variant="contained"
          size="medium"
          onClick={() => window.location.href = signupUrl}
          sx={{
            backgroundColor: "#1A1A1A !important",
            color: "#FFFFFF !important",
            fontWeight: 700,
            px: { xs: 2.5, md: 3 },
            py: { xs: 1, md: 1.25 },
            fontSize: { xs: "0.85rem", md: "0.95rem" },
            borderRadius: 50,
            cursor: "pointer",
            boxShadow: "0 8px 24px rgba(0,0,0,0.25)",
            "&:hover": {
              backgroundColor: "#333333 !important",
              transform: "translateY(-2px)",
              boxShadow: "0 12px 32px rgba(0,0,0,0.35)",
            },
            transition: "all 0.3s ease",
          }}
        >
          Start Free Trial
        </MKButton>
      </MKBox>
    </Fade>
  );
}

// Navigation routes for RingFoods
const navRoutes = [
  {
    name: "features",
    href: "#features",
    icon: "",
  },
  {
    name: "how it works",
    href: "#how-it-works",
    icon: "",
  },
  {
    name: "pricing",
    href: "#pricing",
    icon: "",
  },
  {
    name: "faq",
    href: "#faq",
    icon: "",
  },
  {
    name: "blog",
    href: "/blog",
    icon: "",
  },
];

function RingFoodsHomepage() {
  const signupUrl = useSignupUrl();
  const [showStickyCTA, setShowStickyCTA] = useState(false);

  // One description string feeds name="description", og:description and
  // twitter:description — they have to agree, and the trial length in them is
  // promo-dependent, so it comes from the same helper the visible copy uses.
  const metaDescription = `RingFoods is an AI voice agent that answers your restaurant's phone 24/7 — reservations, takeout orders and FAQs handled. ${getDisplayTrialDays()}-day free trial, 30-minute setup.`;

  // Structured data schemas.
  //
  // parentOrganization + sameAs with the sister brand's URL tell Google that
  // ringfoods.com and ringoperator.com are both operated by Ring AI Inc.
  // (same parent entity) and are related properties. This is the canonical
  // way to signal cross-domain brand relationships for SEO — stronger than
  // just a footer link alone.
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": "https://www.ringfoods.com/#organization",
    name: "RingFoods",
    legalName: "Ring AI Inc.",
    url: "https://www.ringfoods.com",
    logo: "https://www.ringfoods.com/og-image.png",
    description: "AI-powered stress shield for restaurants. Handles every phone call so your team can focus on guests.",
    foundingDate: "2024",
    email: "support@ringfoods.com",
    contactPoint: {
      "@type": "ContactPoint",
      contactType: "Customer Support",
      email: "support@ringfoods.com",
      availableLanguage: ["English"],
    },
    parentOrganization: {
      "@type": "Organization",
      name: "Ring AI Inc.",
      url: "https://www.ringfoods.com",
      // Ring AI Inc. operates both RingFoods and RingOperator.
      subOrganization: [
        { "@type": "Organization", name: "RingFoods", url: "https://www.ringfoods.com" },
        { "@type": "Organization", name: "RingOperator", url: "https://www.ringoperator.com" },
      ],
    },
    sameAs: [
      "https://www.ringfoods.com",
      // Sister brand — search engines treat cross-linked sameAs as a strong
      // signal of a shared entity across domains.
      "https://www.ringoperator.com",
      "https://x.com/ringfoods",
      "https://www.instagram.com/ringfoodsdotcom",
      "https://www.youtube.com/@ringfoodsofficial",
      "https://www.linkedin.com/in/ring-foods-5841a83b9",
    ],
  };

  const websiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": "https://www.ringfoods.com/#website",
    url: "https://www.ringfoods.com",
    name: "RingFoods",
    description: "AI Voice Agent for Restaurants - Stop Losing Staff and Customers to Phone Stress",
    publisher: { "@id": "https://www.ringfoods.com/#organization" },
  };

  const softwareSchema = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "@id": "https://www.ringfoods.com/#software",
    name: "RingFoods AI Voice Agent",
    applicationCategory: "BusinessApplication",
    operatingSystem: "Web-based",
    description: "AI-powered stress shield for restaurants. Handles calls 24/7, absorbs angry callers, speaks every language, and books reservations while your team focuses on guests.",
    url: "https://www.ringfoods.com",
    image: "https://www.ringfoods.com/og-image.png",
    offers: {
      "@type": "Offer",
      price: "100.00",
      priceCurrency: "USD",
      description: "Plans start at $100/month with 200 minutes included. Overage varies by plan: $0.35 (Basic), $0.30 (Professional), $0.25 (Enterprise) per minute.",
      availability: "https://schema.org/InStock",
    },
    featureList: [
      "24/7 AI Phone Answering",
      "Complaint & De-escalation Handling",
      "Multilingual Support",
      "Real-time Google Calendar Integration",
      "Smart Table Management",
      "POS Integration (Square, Clover, Toast, Lightspeed)",
      "OpenTable Integration",
      "10 Premium AI Voice Options",
      "Custom AI Prompts",
      "Call Transcripts & History",
      "Call Recordings & Playback",
      "Customer Feedback Management",
      "Special & Holiday Hours",
      "Automated Cancellation Notification Calls",
      "Call Transfer to Human",
      "Customer SMS Confirmations",
      "Free Restaurant Webpage (Online Reservations, Ordering & Feedback)",
      "Waitlist & Queue Management with Live Position",
      "Printable QR Code Poster",
      "Spam Call Blocking",
      "Mid-Call SMS Links",
      "Call Quality Scoring",
      "Real-time Analytics Dashboard",
      "Regulatory Compliance (FCC/CRTC/ICO/ACMA)",
    ],
    applicationSubCategory: "Restaurants Management Software",
    softwareVersion: APP_VERSION,
    provider: { "@id": "https://www.ringfoods.com/#organization" },
  };

  // FAQPage, built from the same `faqs` array the FAQ section renders. The
  // homepage has always shown eight Q&As; it just never told Google they were
  // Q&As. No new copy, no risk of markup drifting from the page — if a question
  // is edited in the section, the schema follows.
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "@id": "https://www.ringfoods.com/#faq",
    mainEntity: faqs.map((item) => ({
      "@type": "Question",
      name: item.q,
      acceptedAnswer: { "@type": "Answer", text: item.a },
    })),
  };

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": "https://www.ringfoods.com/#service",
    name: "AI Voice Agent for Restaurants",
    description: "AI stress shield for restaurants. Handles every phone call including complaints, after-hours inquiries, and multilingual callers.",
    provider: { "@id": "https://www.ringfoods.com/#organization" },
    serviceType: "Restaurants Technology Service",
    areaServed: [
      { "@type": "Country", name: "United States" },
      { "@type": "Country", name: "Canada" },
      { "@type": "Country", name: "United Kingdom" },
      { "@type": "Country", name: "Australia" },
      { "@type": "Country", name: "New Zealand" },
    ],
    hasOfferCatalog: {
      "@type": "OfferCatalog",
      name: "RingFoods Services",
      itemListElement: [
        {
          "@type": "Offer",
          itemOffered: {
            "@type": "Service",
            name: "AI Voice Agent Subscription",
            description: "Monthly subscription for AI-powered restaurant phone handling, complaint management, and reservation booking",
          },
          price: "100.00",
          priceCurrency: "USD",
        },
      ],
    },
  };

  // Scroll listener for sticky CTA
  useEffect(() => {
    const handleScroll = () => {
      setShowStickyCTA(window.scrollY > window.innerHeight);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    console.log(`${APP_BRAND} ${APP_VERSION}`);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <>
      <Helmet>
        {/* The <title> leads with what people search for, not with the
            positioning line. "Stop Losing Staff and Customers to Phone Stress"
            contained none of the phrases the Ads account buys clicks on
            ("ai voice agent restaurant", "ai receptionist", "restaurant
            answering service"), which is both why the page was invisible for
            them organically and why three keywords carried a low Quality
            Score — landing-page relevance is most of that score. The emotional
            line still leads the H1, where it converts. */}
        <title>AI Voice Agent for Restaurants — 24/7 Phone Answering &amp; Reservations | RingFoods</title>
        <meta name="description" content={metaDescription} />
        <meta name="keywords" content="ringfoods, ring foods, AI voice agent for restaurants, restaurant voice agent, AI receptionist for restaurants, restaurant AI receptionist, restaurant phone answering, AI phone answering for restaurants, restaurant answering service, after hours answering restaurant, restaurant call handling, AI agent for restaurant reservations, restaurant reservation AI, automated restaurant reservations, restaurant phone automation, multilingual restaurant phone, AI complaint handling, restaurant staff burnout, restaurant phone stress, restaurant call transcripts, restaurant analytics dashboard, restaurant order management" />
        <link rel="canonical" href="https://www.ringfoods.com/" />
        <link rel="alternate" hreflang="en-US" href="https://www.ringfoods.com/" />
        <link rel="alternate" hreflang="en-CA" href="https://www.ringfoods.com/" />
        <link rel="alternate" hreflang="en-GB" href="https://www.ringfoods.com/" />
        <link rel="alternate" hreflang="en-AU" href="https://www.ringfoods.com/" />
        <link rel="alternate" hreflang="en-NZ" href="https://www.ringfoods.com/" />
        <link rel="alternate" hreflang="x-default" href="https://www.ringfoods.com/" />
        <meta
          property="og:title"
          content="AI Voice Agent for Restaurants — 24/7 Phone Answering & Reservations | RingFoods"
        />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.ringfoods.com/" />
        <meta property="og:image" content="https://www.ringfoods.com/og-image.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@ringfoods" />
        <meta
          name="twitter:title"
          content="AI Voice Agent for Restaurants — 24/7 Phone Answering & Reservations | RingFoods"
        />
        <meta name="twitter:description" content={metaDescription} />
        <meta name="twitter:image" content="https://www.ringfoods.com/og-image.png" />
        <script type="application/ld+json">{JSON.stringify(organizationSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(websiteSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(softwareSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(serviceSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <PromoBanner />
      <PromoPopup />

      <DefaultNavbar
        brand={APP_BRAND}
        routes={navRoutes}
        action={{
          type: "external",
          route: signupUrl,
          label: "Login",
          color: "dark",
        }}
        transparent
        light
      />

      <MKBox component="main">
      {/* Hero Section */}
      <Hero />

      {/* Pain Points + StressShield Card */}
      <Card
        sx={{
          mx: { xs: 1, sm: 2, lg: 3 },
          mt: { xs: -6, sm: -10, md: -12 },
          mb: 4,
          backgroundColor: ({ palette: { white }, functions: { rgba } }) => rgba(white.main, 0.8),
          backdropFilter: "saturate(200%) blur(30px)",
          boxShadow: ({ boxShadows: { xxl } }) => xxl,
          borderRadius: 2,
          overflow: "hidden",
        }}
      >
        {/* Pain Points Section */}
        <PainPoints />
      </Card>

      {/* Stress Shield Section (dark background, standalone) -- directly after pain */}
      <StressShield />

      {/* Image Banner */}
      <ImageBanner
        imageUrl="https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=1200&q=60"
        title="Your Team Deserves Better Than Phone Stress"
        subtitle="AI-powered call handling that protects your staff and your reputation"
      />

      {/* Card 2a: Features only */}
      <Card
        sx={{
          mx: { xs: 1, sm: 2, lg: 3 },
          my: 4,
          backgroundColor: ({ palette: { white }, functions: { rgba } }) => rgba(white.main, 0.95),
          boxShadow: ({ boxShadows: { xxl } }) => xxl,
          borderRadius: 2,
          overflow: "hidden",
        }}
      >
        <Features />
        <FreeBusinessPage />
      </Card>

      {/* Personalization Section (dark, standalone) */}
      <Personalization />

      {/* Card 2b: Visibility + HowItWorks + ROI */}
      <Card
        sx={{
          mx: { xs: 1, sm: 2, lg: 3 },
          my: 4,
          backgroundColor: ({ palette: { white }, functions: { rgba } }) => rgba(white.main, 0.95),
          boxShadow: ({ boxShadows: { xxl } }) => xxl,
          borderRadius: 2,
          overflow: "hidden",
        }}
      >
        <Visibility />
        <HowItWorks />
        <ROICalculator />
      </Card>

      {/* Image Banner */}
      <ImageBanner
        imageUrl="https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=1200&q=60"
        title="Restaurants That Sleep Better at Night"
        subtitle="Protecting teams and delighting callers around the clock"
      />

      <Card
        sx={{
          mx: { xs: 1, sm: 2, lg: 3 },
          my: 4,
          backgroundColor: ({ palette: { white }, functions: { rgba } }) => rgba(white.main, 0.95),
          boxShadow: ({ boxShadows: { xxl } }) => xxl,
          borderRadius: 2,
          overflow: "hidden",
        }}
      >
        {/* Pricing Section */}
        <Pricing />

        {/* Custom Features Section */}
        <CustomFeatures />

        {/* Integrations Section */}
        <Integrations />

        {/* FAQ Section */}
        <FAQ />
      </Card>

      {/* From Our Blog Section */}
      <MKBox component="section" py={{ xs: 6, md: 10 }} sx={{ backgroundColor: grey100 }}>
        <Container>
          <Grid container spacing={3} sx={{ mb: { xs: 4, md: 6 } }}>
            <Grid item xs={12} textAlign="center">
              <MKTypography
                variant="h2"
                fontWeight="bold"
                color="primary"
                sx={{ mb: 2, fontSize: { xs: "1.85rem", sm: "2.25rem", md: "2.5rem" } }}
              >
                From Our Blog
              </MKTypography>
              <MKTypography variant="body1" color="text" sx={{ fontSize: { xs: "1rem", md: "1.125rem" } }}>
                Tips and insights for restaurant owners navigating phone stress, staffing, and technology.
              </MKTypography>
            </Grid>
          </Grid>
          <Grid container spacing={3}>
            {[
              {
                slug: "ai-voice-agent-vs-answering-service-restaurants",
                title: "AI Voice Agent vs Traditional Answering Service",
                desc: "Cost, availability, and accuracy — which option actually books reservations?",
              },
              {
                slug: "how-much-revenue-do-restaurants-lose-from-missed-phone-calls",
                title: "How Much Revenue Do Restaurants Lose from Missed Calls?",
                desc: "The average restaurant misses 20% of calls during peak hours. Here's what that costs.",
              },
              {
                slug: "ai-phone-answering-vs-hiring-a-receptionist-what-restaurant-owners-need-to-know",
                title: "AI Phone Answering vs Hiring a Receptionist",
                desc: "A cost and capability comparison for restaurant owners.",
              },
              {
                slug: "how-to-reduce-no-shows-at-your-restaurant",
                title: "How to Reduce No-Shows at Your Restaurant",
                desc: "Practical strategies including automated confirmations.",
              },
            ].map((article) => (
              <Grid item xs={12} sm={6} md={3} key={article.slug}>
                <MKBox
                  component={Link}
                  to={`/blog/${article.slug}`}
                  sx={{
                    display: "block",
                    p: { xs: 2.5, md: 3 },
                    borderRadius: 2,
                    backgroundColor: "white",
                    border: "1px solid #E5E5E5",
                    textDecoration: "none",
                    height: "100%",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
                    },
                  }}
                >
                  <MKTypography variant="h6" fontWeight="bold" color="dark" sx={{ mb: 1, fontSize: { xs: "0.95rem", md: "1rem" } }}>
                    {article.title}
                  </MKTypography>
                  <MKTypography variant="body2" color="text" sx={{ opacity: 0.75, lineHeight: 1.6, mb: 2 }}>
                    {article.desc}
                  </MKTypography>
                  <MKTypography
                    variant="body2"
                    fontWeight={600}
                    sx={{
                      color: brandPrimary,
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                    }}
                  >
                    Read more <ArrowForwardIcon sx={{ fontSize: 16 }} />
                  </MKTypography>
                </MKBox>
              </Grid>
            ))}
          </Grid>
          <MKBox textAlign="center" sx={{ mt: { xs: 4, md: 5 } }}>
            <MKTypography
              component={Link}
              to="/blog"
              variant="body1"
              fontWeight={600}
              sx={{
                color: brandPrimary,
                textDecoration: "none",
                display: "inline-flex",
                alignItems: "center",
                gap: 0.5,
                "&:hover": { opacity: 0.7 },
              }}
            >
              View All Articles <ArrowForwardIcon sx={{ fontSize: 18 }} />
            </MKTypography>
          </MKBox>
        </Container>
      </MKBox>

      {/* Final CTA Section */}
      <FinalCTA />
      </MKBox>

      <SiteFooter isHomepage />

      {/* Sticky CTA */}
      <StickyCTA visible={showStickyCTA} />
    </>
  );
}

export default RingFoodsHomepage;
